// Mapeamento central Lucide → Phosphor (peso "light") — PEDIDO_EMERGENT_ICONES_PREMIUM.
//
// Todas as telas importam ícones DAQUI, não mais de "lucide-react", para
// manter a UI com o traço Phosphor Light sem ter que repintar cada arquivo.
// A API é a de Lucide (mesmo nome, props `size`, `className`, `color`,
// `strokeWidth`, `data-testid`...). Internamente delegamos para o Phosphor
// componente equivalente com `weight="light"` por padrão. Tamanho é 1em por
// padrão (herda do `w-N h-N` do Tailwind, igual Lucide fazia).
//
// Observações:
// - A cor segue o `color` ou `currentColor` do texto ao redor (igual Lucide).
// - `strokeWidth` do Lucide é ignorado — o peso agora é controlado por `weight`.
// - Nomes que o Phosphor não cobre caem no fallback mais próximo descrito na
//   tabela do pedido (ex.: PackageCheck → Package, ShieldQuestion → Shield).
import { forwardRef } from "react";
import {
  House,
  Kanban,
  Student,
  UsersThree,
  CalendarDots,
  CalendarBlank,
  CalendarPlus,
  CalendarX,
  Warning,
  WarningCircle,
  TrendUp,
  TrendDown,
  CurrencyCircleDollar,
  ChartBar,
  ChartLine,
  AddressBook,
  MapPin,
  Wallet,
  Bell,
  MagnifyingGlass,
  GearSix,
  SignOut,
  PencilSimple,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  CaretDown,
  Plus,
  X,
  Trash,
  Phone,
  EnvelopeSimple,
  FileText,
  FileXls,
  FloppyDisk,
  Stack,
  Target,
  Camera,
  Check,
  Checks,
  CheckCircle,
  Circle,
  ClipboardText,
  Clock,
  IdentificationCard,
  ArrowBendDownLeft,
  Crosshair,
  Download,
  CloudArrowDown,
  CloudArrowUp,
  ArrowSquareOut,
  Eye,
  EyeSlash,
  GitBranch,
  DotsSixVertical,
  Image as PhImage,
  Tray,
  Info,
  Key,
  SquaresFour,
  LinkSimple,
  ListChecks,
  CircleNotch,
  Medal,
  Megaphone,
  List as ListIcon,
  ChatCircle,
  ChatText,
  ChatCircleDots,
  Minus,
  DotsThree,
  DotsThreeVertical,
  Package,
  PauseCircle,
  Percent,
  Receipt,
  ArrowClockwise,
  PaperPlaneTilt,
  HardDrives,
  ShieldWarning,
  ShieldCheck,
  Shield,
  ShieldSlash,
  Sliders,
  Sparkle,
  Star,
  ThumbsUp,
  Trophy,
  Truck,
  ArrowUUpLeft,
  Upload,
  User,
  UserGear,
  UserPlus,
  UserMinus,
  VideoCamera,
  XCircle,
  Briefcase,
  Buildings,
  Calculator,
} from "@phosphor-icons/react";

// Fábrica: devolve um componente que delega ao ícone Phosphor com weight=light.
// Passa `size` só se o chamador informar — caso contrário, herda via CSS.
function make(PhComp) {
  const Comp = forwardRef(function PhWrapper(
    { size, weight, className, style, color, strokeWidth: _sw, ...rest },
    ref,
  ) {
    const sizeProp = size == null ? "1em" : size;
    return (
      <PhComp
        ref={ref}
        size={sizeProp}
        weight={weight || "light"}
        className={className}
        style={style}
        color={color}
        {...rest}
      />
    );
  });
  Comp.displayName = `Icon(${PhComp.displayName || PhComp.name || "?"})`;
  return Comp;
}

// -------- Exports (nomes Lucide → componentes Phosphor Light) --------
export const LayoutGrid = make(House);
export const Home = make(House);
export const SquareKanban = make(Kanban);
export const KanbanSquare = make(Kanban);
export const Users = make(UsersThree);
export const Users2 = make(UsersThree);
export const UsersRound = make(UsersThree);
export const CalendarClock = make(CalendarDots);
export const CalendarDays = make(CalendarBlank);
export const CalendarPlus_ = make(CalendarPlus);
export { CalendarPlus_ as CalendarPlus };
export const CalendarX2 = make(CalendarX);
export const AlertTriangle = make(Warning);
export const TriangleAlert = make(Warning);
export const AlertCircle = make(WarningCircle);
export const TrendingUp = make(TrendUp);
export const TrendingDown = make(TrendDown);
export const DollarSign = make(CurrencyCircleDollar);
export const FileBarChart = make(ChartBar);
export const FileChartColumn = make(ChartBar);
export const LineChart = make(ChartLine);
export const SquareUser = make(AddressBook);
export const Contact = make(IdentificationCard);
export const MapPin_ = make(MapPin);
export { MapPin_ as MapPin };
export const Wallet_ = make(Wallet);
export { Wallet_ as Wallet };
export const Bell_ = make(Bell);
export { Bell_ as Bell };
export const Search = make(MagnifyingGlass);
export const Settings = make(GearSix);
export const LogOut = make(SignOut);
export const Pencil = make(PencilSimple);
export const ArrowLeft_ = make(ArrowLeft);
export { ArrowLeft_ as ArrowLeft };
export const ArrowRight_ = make(ArrowRight);
export { ArrowRight_ as ArrowRight };
export const ArrowUp_ = make(ArrowUp);
export { ArrowUp_ as ArrowUp };
export const ArrowDown_ = make(ArrowDown);
export { ArrowDown_ as ArrowDown };
export const ChevronLeft = make(CaretLeft);
export const ChevronRight = make(CaretRight);
export const ChevronUp = make(CaretUp);
export const ChevronDown = make(CaretDown);
export const Plus_ = make(Plus);
export { Plus_ as Plus };
export const X_ = make(X);
export { X_ as X };
export const Trash2 = make(Trash);
export const Phone_ = make(Phone);
export { Phone_ as Phone };
export const Mail = make(EnvelopeSimple);
export const FileText_ = make(FileText);
export { FileText_ as FileText };
export const FileSpreadsheet = make(FileXls);
export const Save = make(FloppyDisk);
export const Layers = make(Stack);
export const Target_ = make(Target);
export { Target_ as Target };
export const Camera_ = make(Camera);
export { Camera_ as Camera };
export const Check_ = make(Check);
export { Check_ as Check };
export const CheckCheck = make(Checks);
export const CheckCircle2 = make(CheckCircle);
export const Circle_ = make(Circle);
export { Circle_ as Circle };
export const ClipboardList = make(ClipboardText);
export const Clock_ = make(Clock);
export { Clock_ as Clock };
export const CornerDownLeft = make(ArrowBendDownLeft);
export const Crosshair_ = make(Crosshair);
export { Crosshair_ as Crosshair };
export const Download_ = make(Download);
export { Download_ as Download };
export const DownloadCloud = make(CloudArrowDown);
export const UploadCloud = make(CloudArrowUp);
export const Upload_ = make(Upload);
export { Upload_ as Upload };
export const ExternalLink = make(ArrowSquareOut);
export const Eye_ = make(Eye);
export { Eye_ as Eye };
export const EyeOff = make(EyeSlash);
export const GitBranch_ = make(GitBranch);
export { GitBranch_ as GitBranch };
export const GripVertical = make(DotsSixVertical);
export const Image = make(PhImage);
export const Inbox = make(Tray);
export const Info_ = make(Info);
export { Info_ as Info };
export const KeyRound = make(Key);
export const LayoutDashboard = make(SquaresFour);
export const Link2 = make(LinkSimple);
export const ListTodo = make(ListChecks);
export const Loader2 = make(CircleNotch);
export const Medal_ = make(Medal);
export { Medal_ as Medal };
export const Megaphone_ = make(Megaphone);
export { Megaphone_ as Megaphone };
export const Menu = make(ListIcon);
export const MessageCircle = make(ChatCircle);
export const MessageSquare = make(ChatText);
export const MessageSquareText = make(ChatText);
export const MessageSquareWarning = make(ChatCircleDots);
export const Minus_ = make(Minus);
export { Minus_ as Minus };
export const MoreHorizontal = make(DotsThree);
export const MoreVertical = make(DotsThreeVertical);
export const PackageCheck = make(Package);
export const PauseCircle_ = make(PauseCircle);
export { PauseCircle_ as PauseCircle };
export const Percent_ = make(Percent);
export { Percent_ as Percent };
export const Receipt_ = make(Receipt);
export { Receipt_ as Receipt };
export const RefreshCw = make(ArrowClockwise);
export const Send = make(PaperPlaneTilt);
export const Server = make(HardDrives);
export const Database = make(HardDrives);
export const ShieldAlert = make(ShieldWarning);
export const ShieldCheck_ = make(ShieldCheck);
export { ShieldCheck_ as ShieldCheck };
export const ShieldQuestion = make(Shield);
export const ShieldX = make(ShieldSlash);
export const SlidersHorizontal = make(Sliders);
export const Sparkles = make(Sparkle);
export const Star_ = make(Star);
export { Star_ as Star };
export const ThumbsUp_ = make(ThumbsUp);
export { ThumbsUp_ as ThumbsUp };
export const Trophy_ = make(Trophy);
export { Trophy_ as Trophy };
export const Truck_ = make(Truck);
export { Truck_ as Truck };
export const Undo2 = make(ArrowUUpLeft);
export const User_ = make(User);
export { User_ as User };
export const UserCog = make(UserGear);
export const UserPlus_ = make(UserPlus);
export { UserPlus_ as UserPlus };
export const UserX = make(UserMinus);
export const Video = make(VideoCamera);
export const XCircle_ = make(XCircle);
export { XCircle_ as XCircle };
export const Briefcase_ = make(Briefcase);
export { Briefcase_ as Briefcase };
export const Building2 = make(Buildings);
export const Calculator_ = make(Calculator);
export const GraduationCap = make(Student);
export { Calculator_ as Calculator };
